import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ExternalLink,
  MessageSquare,
  CheckCircle2,
  Circle,
  BookOpen,
  Cable,
  Box,
  FileText,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getProject,
  projectProgressPercent,
  type EngineeringProject,
  type ProjectPhase,
} from "@/lib/prometheusProjectsStore";
import { chatIdForProject, touchChatEntry } from "@/lib/prometheusChatIndex";
import { ProjectWiringPanel } from "@/components/prometheus/os/ProjectWiringPanel";
import { ArtifactDespiecePanel } from "@/components/prometheus/os/ArtifactDespiecePanel";
import { ArtifactBomChecklist } from "@/components/prometheus/os/ArtifactBomChecklist";
import { ArtifactSafetyPanel } from "@/components/prometheus/os/ArtifactSafetyPanel";
import { ArtifactAsset3dState } from "@/components/prometheus/os/ArtifactAsset3dState";

type WorkspaceTab = "resumen" | "despiece" | "wiring" | "seguridad" | "bitacora";

const TABS: { id: WorkspaceTab; label: string; icon: typeof FileText }[] = [
  { id: "resumen", label: "Resumen", icon: FileText },
  { id: "despiece", label: "3D/Despiece", icon: Box },
  { id: "wiring", label: "Wiring", icon: Cable },
  { id: "seguridad", label: "Seguridad", icon: ShieldCheck },
  { id: "bitacora", label: "Bitácora", icon: BookOpen },
];

const STATUS_LABEL: Record<string, string> = {
  "in-progress": "En progreso",
  "idea-future": "Idea / futuro",
  completed: "Completado",
};

function phaseEntries(project: EngineeringProject): { key: string; phase: ProjectPhase }[] {
  if (!project.phases) return [];
  return Object.entries(project.phases)
    .filter(([, phase]) => phase != null)
    .map(([key, phase]) => ({ key, phase: phase as ProjectPhase }));
}

function continueInChat(project: EngineeringProject) {
  const prompt = `Prometeo, trabajemos juntos en el proyecto: "${project.title}"${
    project.discipline ? ` (${project.discipline})` : ""
  }.
Mi objetivo es desarrollarlo paso a paso. Explícame de forma práctica y sin palabras complicadas:
1. ¿Qué es lo primero que debo hacer para comenzar hoy?
2. ¿Qué insumos o herramientas necesito a la mano?
3. ¿Cuál es el orden lógico para avanzar sin perderme?`;

  touchChatEntry({
    id: chatIdForProject(project.id),
    projectId: project.id,
    title: project.title,
  });
  window.dispatchEvent(new Event("prometheus-chat-index-changed"));
  // Leave observe-only Projects view → Chat (edits / continue live here)
  window.dispatchEvent(
    new CustomEvent("prometheus-os-section", { detail: { section: "chats" } })
  );
  window.dispatchEvent(
    new CustomEvent("open-prometheus-chat", {
      detail: {
        projectId: project.id,
        characterId: "prometheus",
        initialPrompt: prompt,
        autoSend: true,
      },
    })
  );
}

export type ProjectWorkspacePanelProps = {
  projectId: string;
  className?: string;
};

function parseWorkspaceTab(raw: string | null): WorkspaceTab | null {
  if (
    raw === "resumen" ||
    raw === "despiece" ||
    raw === "wiring" ||
    raw === "seguridad" ||
    raw === "bitacora"
  ) {
    return raw;
  }
  return null;
}

export function ProjectWorkspacePanel({ projectId, className }: ProjectWorkspacePanelProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = parseWorkspaceTab(searchParams.get("tab"));
  const [tab, setTab] = useState<WorkspaceTab>(tabFromUrl || "resumen");
  const project = useMemo(() => getProject(projectId), [projectId]);

  useEffect(() => {
    if (tabFromUrl) setTab(tabFromUrl);
  }, [tabFromUrl, projectId]);

  const selectTab = (id: WorkspaceTab) => {
    setTab(id);
    const next = new URLSearchParams(searchParams);
    if (id === "resumen") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  if (!project) {
    return (
      <div className={cn("flex h-full flex-col p-4 font-mono-tech", className)}>
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-2">
          Proyecto
        </p>
        <p className="text-sm text-muted-foreground">
          No se encontró el proyecto <span className="text-foreground">{projectId}</span>.
        </p>
      </div>
    );
  }

  const progress = projectProgressPercent(project);
  const statusLabel =
    (project.status && STATUS_LABEL[project.status]) || project.status || "Sin estado";

  return (
    <div className={cn("flex h-full min-h-0 flex-col font-mono-tech", className)}>
      <header className="shrink-0 border-b border-border/60 px-4 py-3 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[9px] uppercase tracking-[0.2em] text-primary font-bold mb-1">
              Proyecto · solo lectura
            </p>
            <h2 className="font-serif-display text-lg sm:text-xl text-foreground leading-snug">
              {project.title}
            </h2>
            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
              Observa despiece, wiring, pasos y bitácora. Para editar o avanzar, continúa en el chat.
            </p>
          </div>
          <button
            type="button"
            onClick={() => continueInChat(project)}
            className="inline-flex items-center gap-2 shrink-0 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-[11px] font-bold hover:opacity-90 cursor-pointer"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Continuar en chat
          </button>
        </div>
      </header>

      <div className="shrink-0 flex gap-0.5 overflow-x-auto border-b border-border/60 px-2 py-1.5">
        {TABS.map(({ id, label, icon: Icon }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => selectTab(id)}
              className={cn(
                "flex items-center gap-1 shrink-0 rounded-md px-2 py-1 text-[10px] transition-colors cursor-pointer",
                active
                  ? "bg-primary/15 text-primary font-bold"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
              )}
            >
              <Icon className="w-3 h-3" />
              <span className="whitespace-nowrap">{label}</span>
            </button>
          );
        })}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 text-sm">
        {tab === "resumen" && (
          <div className="space-y-4">
            {project.description ? (
              <p className="text-muted-foreground leading-relaxed text-[13px]">
                {project.description}
              </p>
            ) : (
              <p className="text-muted-foreground text-[13px]">Sin descripción.</p>
            )}
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-[12px]">
              <dt className="text-muted-foreground">Estado</dt>
              <dd className="text-foreground font-medium">{statusLabel}</dd>
              <dt className="text-muted-foreground">Progreso</dt>
              <dd className="text-foreground font-medium">{progress}%</dd>
              {project.discipline ? (
                <>
                  <dt className="text-muted-foreground">Disciplina</dt>
                  <dd className="text-foreground">{project.discipline}</dd>
                </>
              ) : null}
            </dl>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary/80 transition-[width]"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Despiece, wiring y bitácora viven aquí en el Artifact. El Taller legacy queda solo
              si necesitas la suite completa antigua.
            </p>
            <Link
              to="/taller?station=projects"
              className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground/70 hover:text-muted-foreground hover:underline"
            >
              Abrir suite Taller (legacy)
              <ExternalLink className="w-3 h-3" />
            </Link>
            <div className="border-t border-border/60 pt-4">
              <PasosTab project={project} />
            </div>
          </div>
        )}

        {tab === "despiece" && (
          <div className="space-y-5">
            <ArtifactAsset3dState
              key={`asset-${project.id}`}
              projectId={project.id}
              projectTitle={project.title}
              discipline={project.discipline}
            />
            <ArtifactDespiecePanel key={project.id} projectTitle={project.title} />
            <div className="border-t border-border/60 pt-4">
              <ArtifactBomChecklist key={project.id} projectTitle={project.title} />
            </div>
          </div>
        )}

        {tab === "wiring" && <ProjectWiringPanel projectId={projectId} />}

        {tab === "seguridad" && (
          <ArtifactSafetyPanel
            projectId={project.id}
            projectTitle={project.title}
            discipline={project.discipline}
          />
        )}

        {tab === "bitacora" && <BitacoraTab project={project} />}
      </div>
    </div>
  );
}

function PasosTab({ project }: { project: EngineeringProject }) {
  const entries = phaseEntries(project);
  if (entries.length === 0) {
    return (
      <p className="text-[13px] text-muted-foreground">
        Este proyecto aún no tiene fases/pasos definidos.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {entries.map(({ key, phase }) => (
        <section key={key}>
          <h3 className="text-[10px] uppercase tracking-[0.14em] text-primary font-bold mb-2">
            {phase.label || phase.name || key}
          </h3>
          {!phase.steps || phase.steps.length === 0 ? (
            <p className="text-[12px] text-muted-foreground pl-1">Sin pasos.</p>
          ) : (
            <ul className="space-y-1.5">
              {phase.steps.map((step) => (
                <li
                  key={step.id}
                  className="flex items-start gap-2 text-[12px] text-foreground/90"
                >
                  {step.completed ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                  ) : (
                    <Circle className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  )}
                  <span className={cn(step.completed && "text-muted-foreground line-through")}>
                    {step.title}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

function BitacoraTab({ project }: { project: EngineeringProject }) {
  const logs = project.logs ?? [];
  if (logs.length === 0) {
    return (
      <p className="text-[13px] text-muted-foreground">
        Sin entradas de bitácora todavía.
      </p>
    );
  }

  return (
    <ul className="space-y-3">
      {logs.map((log) => (
        <li
          key={log.id}
          className="rounded-lg border border-border/60 bg-card/40 px-3 py-2"
        >
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <p className="text-[12px] font-bold text-foreground">{log.title}</p>
            <time className="text-[9px] text-muted-foreground shrink-0">{log.timestamp}</time>
          </div>
          <p className="text-[12px] text-muted-foreground leading-relaxed whitespace-pre-wrap">
            {log.content}
          </p>
          {log.category ? (
            <p className="mt-1.5 text-[9px] uppercase tracking-wider text-primary/80">
              {log.category}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
